import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import { LanguageProvider, useLanguage } from "@/shared/components/shared/LanguageContext";
import LanguageSwitcher from "@/shared/components/shared/LanguageSwitcher";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { isStaffUser } from "@/shared/utils/permissions";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, ArrowDownToLine, ArrowUpFromLine, Building2,
  Server, Route, Webhook, FileText, Settings, ChevronLeft,
  Search, User, LogOut, Shield, Activity,
  X, Users, Zap, Key, Globe, Cpu, Bell, Menu,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import NotificationDropdown from "@/shared/components/shared/NotificationDropdown";
import useInfrastructureStatus from "@/shared/hooks/useInfrastructureStatus";

/* ══════════════════════════════════════════
   GLOBAL CSS
══════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Geist+Mono:wght@400;500;600&family=Syne:wght@600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --cyan: #22d3ee;
  --cyan4: #67e8f9;
  --emerald: #34d399;
  --rose: #fb7185;
  --n950: #060c1a;
  --n900: #070e1e;
  --n850: #080f20;
  --n800: #091122;
  --b1: rgba(34,211,238,0.08);
  --b2: rgba(34,211,238,0.18);
  --t1: #e8f4ff;
  --t2: #a8c4e0;
  --t3: #5a82a0;
  --t4: #2e4a66;
  --t5: #1a2e44;
  --fd: 'Outfit', sans-serif;
  --fs: 'Syne', sans-serif;
  --fm: 'Geist Mono', monospace;
  --out: cubic-bezier(0.22,1,0.36,1);
  --spring: cubic-bezier(0.34,1.56,0.64,1);
}

html { scroll-behavior: smooth; }
body { background: var(--n950); color: var(--t1); font-family: var(--fd); -webkit-font-smoothing: antialiased; }
html, body { overflow-x: hidden; max-width: 100vw; }
*::-webkit-scrollbar { width: 3px; height: 3px; }
*::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.15); border-radius: 99px; }
*::-webkit-scrollbar-track { background: transparent; }

/* ══ SIDEBAR ══ */
.ad-sb {
  position: fixed; top: 0; left: 0; bottom: 0;
  display: flex; flex-direction: column;
  background:
    radial-gradient(ellipse 140% 35% at 50% -1%, rgba(34,211,238,0.1), transparent 55%),
    linear-gradient(180deg, #04091a 0%, #030710 55%, #020610 100%);
  border-right: 1px solid rgba(34,211,238,0.07);
  z-index: 50; overflow: clip;
  overflow-clip-margin: 0;
  transition: width 0.38s cubic-bezier(0.22,1,0.36,1);
  will-change: width;
}
.ad-sb::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.7) 40%, rgba(34,211,238,0.7) 60%, transparent);
  z-index: 5; pointer-events: none;
  animation: sbglow 5s ease-in-out infinite;
}
@keyframes sbglow { 0%,100%{opacity:0.35} 50%{opacity:1} }

/* ── LOGO ── */
.ad-logo {
  height: 70px; display: flex; align-items: center;
  padding: 0 16px; gap: 13px;
  border-bottom: 1px solid rgba(34,211,238,0.07);
  flex-shrink: 0; text-decoration: none;
  position: relative; overflow: hidden; cursor: pointer;
  transition: background 0.22s; z-index: 2;
}
.ad-logo:hover { background: rgba(34,211,238,0.05); }
.ad-orb {
  width: 40px; height: 40px; border-radius: 13px; flex-shrink: 0;
  background: linear-gradient(135deg, rgba(34,211,238,0.2), rgba(59,130,246,0.2));
  border: 1px solid rgba(34,211,238,0.35);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 0 1px rgba(34,211,238,0.25), 0 4px 20px rgba(34,211,238,0.16);
  transition: box-shadow 0.3s, transform 0.4s var(--spring);
  position: relative; overflow: hidden;
}
.ad-orb::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 48%;
  background: rgba(255,255,255,0.12); border-radius: inherit;
}
.ad-orb svg { width: 18px; height: 18px; color: var(--cyan); position: relative; z-index: 1; }
.ad-logo:hover .ad-orb {
  box-shadow: 0 0 0 2px rgba(34,211,238,0.55), 0 6px 28px rgba(34,211,238,0.38);
  transform: scale(1.08) rotate(-7deg);
}
.ad-logo-name { font-family: var(--fs); font-size: 1.15rem; font-weight: 800; color: var(--t1); letter-spacing: -0.02em; white-space: nowrap; line-height: 1; }
.ad-logo-tag { font-family: var(--fm); font-size: 0.5rem; font-weight: 500; color: var(--t4); text-transform: uppercase; letter-spacing: 0.24em; margin-top: 4px; white-space: nowrap; }

/* ── NAV ── */
.ad-nav { flex: 1; padding: 14px 10px 4px; overflow-y: auto; overflow-x: hidden; position: relative; z-index: 2; min-height: 0; }
.ad-nav::-webkit-scrollbar { width: 0; }
.ad-sec-lbl { font-family: var(--fm); font-size: 0.5rem; font-weight: 600; color: rgba(34,211,238,0.22); text-transform: uppercase; letter-spacing: 0.28em; padding: 0 12px; margin: 6px 0 8px; white-space: nowrap; }
.ad-sep { height: 1px; margin: 8px 12px; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.08), transparent); }

/* ── NAV ITEM ── */
.ad-item {
  display: flex; align-items: center; gap: 11px;
  padding: 0 12px; height: 46px; border-radius: 12px; margin-bottom: 3px;
  text-decoration: none;
  font-family: var(--fd); font-size: 0.9rem; font-weight: 500;
  color: var(--t3); border: 1px solid transparent;
  position: relative; overflow: hidden; cursor: pointer; white-space: nowrap;
  transition: color 0.2s, background 0.2s, border-color 0.2s, box-shadow 0.2s;
}
.ad-item::after {
  content: ''; position: absolute; top: 0; left: -130%; width: 70%; bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.09), transparent);
  transform: skewX(-16deg);
  transition: left 0.5s var(--out); pointer-events: none;
}
.ad-item:hover { color: var(--t1); background: rgba(34,211,238,0.07); border-color: rgba(34,211,238,0.14); }
.ad-item:hover::after { left: 160%; }
.ad-item.on { color: #fff; background: linear-gradient(90deg, rgba(34,211,238,0.15) 0%, rgba(34,211,238,0.05) 100%); border-color: rgba(34,211,238,0.22); box-shadow: inset 0 1px 0 rgba(34,211,238,0.15), 0 2px 20px rgba(34,211,238,0.12); }
.ad-item.on::after { display: none; }
.ad-item.on::before { content: ''; position: absolute; left: 0; top: 18%; bottom: 18%; width: 3px; border-radius: 0 3px 3px 0; background: linear-gradient(180deg, var(--cyan4), var(--cyan)); box-shadow: 0 0 14px rgba(34,211,238,0.95), 0 0 6px rgba(34,211,238,0.5); }

.ad-ico { width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.025); transition: background 0.22s, box-shadow 0.22s, transform 0.22s var(--spring); }
.ad-ico svg { width: 15px; height: 15px; }
.ad-item:hover .ad-ico { background: rgba(34,211,238,0.15); box-shadow: 0 0 12px rgba(34,211,238,0.2); transform: scale(1.1); }
.ad-item.on .ad-ico { background: linear-gradient(135deg, rgba(34,211,238,0.25), rgba(34,211,238,0.1)); box-shadow: 0 2px 14px rgba(34,211,238,0.2); }
.ad-item.on .ad-ico svg { filter: drop-shadow(0 0 4px rgba(34,211,238,0.7)); }
.ad-item-txt { flex: 1; white-space: nowrap; letter-spacing: 0.005em; }

.ad-tipwrap { position: relative; }
.ad-tip { position: absolute; left: calc(100% + 14px); top: 50%; transform: translateY(-50%); background: var(--n800); border: 1px solid var(--b2); border-radius: 10px; padding: 6px 14px; font-family: var(--fd); font-size: 0.82rem; font-weight: 600; color: var(--t1); white-space: nowrap; pointer-events: none; opacity: 0; transition: opacity 0.18s; z-index: 100; box-shadow: 0 4px 20px rgba(0,0,0,0.55); }
.ad-tip::before { content: ''; position: absolute; left: -5px; top: 50%; width: 8px; height: 8px; background: var(--n800); border-left: 1px solid var(--b2); border-bottom: 1px solid var(--b2); transform: translateY(-50%) rotate(45deg); }
.ad-tipwrap:hover .ad-tip { opacity: 1; }

/* ── STATUS CARD ── */
.ad-status {
  margin: 0 10px 0; border-radius: 16px;
  background: linear-gradient(145deg, rgba(34,211,238,0.07), rgba(34,211,238,0.02));
  border: 1px solid rgba(34,211,238,0.1);
  position: relative; overflow: hidden; z-index: 2;
  transition: border-color 0.28s, box-shadow 0.28s, transform 0.28s;
  cursor: default;
}
.ad-status:hover {
  border-color: rgba(34,211,238,0.28);
  box-shadow: 0 4px 28px rgba(34,211,238,0.12), 0 0 0 1px rgba(34,211,238,0.08);
  transform: translateY(-2px);
}
.ad-status::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.4), transparent); }
.ad-status::after {
  content: ''; position: absolute; inset: 0; border-radius: 16px;
  background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(34,211,238,0.06), transparent 70%);
  opacity: 0; transition: opacity 0.3s; pointer-events: none;
}
.ad-status:hover::after { opacity: 1; }
.ad-status-in { padding: 14px 16px; }
.ad-status-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.ad-status-lbl { font-family: var(--fm); font-size: 0.52rem; font-weight: 600; color: rgba(34,211,238,0.4); text-transform: uppercase; letter-spacing: 0.22em; }
.ad-status-badge { font-family: var(--fm); font-size: 0.58rem; font-weight: 700; padding: 2px 8px; border-radius: 99px; }
.ad-status-item { display: flex; align-items: center; justify-content: space-between; padding: 5px 0; }
.ad-status-name { font-family: var(--fd); font-size: 0.78rem; font-weight: 500; color: var(--t3); transition: color 0.2s; }
.ad-status:hover .ad-status-name { color: var(--t2); }
.ad-status-val { display: flex; align-items: center; gap: 6px; font-family: var(--fm); font-size: 0.7rem; font-weight: 600; }
.ad-dot-live { width: 6px; height: 6px; border-radius: 50%; animation: livepulse 2.2s ease-in-out infinite; }
@keyframes livepulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.6);opacity:0.4} }

/* ── COLLAPSE BUTTON ── */
.ad-togwrap {
  display: flex; justify-content: center;
  padding: 12px 10px 16px; flex-shrink: 0;
  position: relative; z-index: 6;
  background: linear-gradient(to top, rgba(2,6,16,0.98) 60%, transparent);
}
.ad-tog { display: flex; align-items: center; gap: 8px; padding: 8px 18px; border-radius: 99px; background: rgba(34,211,238,0.07); border: 1px solid rgba(34,211,238,0.16); color: var(--t3); cursor: pointer; position: relative; overflow: hidden; transition: color 0.22s, border-color 0.25s, box-shadow 0.25s, transform 0.22s, background 0.22s; white-space: nowrap; font-family: var(--fd); }
.ad-tog:hover { color: var(--cyan); border-color: rgba(34,211,238,0.5); box-shadow: 0 0 30px rgba(34,211,238,0.2); transform: translateY(-2px) scale(1.04); }
.ad-tog svg { width: 13px; height: 13px; flex-shrink: 0; }
.ad-tog-txt { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; }
.ad-tog.pill { padding: 10px; border-radius: 50%; gap: 0; }
.ad-tog.pill .ad-tog-txt { display: none; }

/* ══ HEADER ══ */
.ad-header {
  height: 70px;
  background: var(--n900);
  border-bottom: 1px solid rgba(34,211,238,0.07);
  display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 30;
}
.ad-header::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg,transparent,rgba(34,211,238,0.09),transparent); pointer-events: none; }

.ad-huser {
  display: flex; align-items: center; gap: 10px;
  background: var(--n800);
  border: 1px solid rgba(34,211,238,0.1);
  border-radius: 14px; padding: 7px 16px 7px 7px; cursor: pointer; color: inherit;
  transition: background 0.22s, border-color 0.22s, box-shadow 0.22s, transform 0.22s;
}
.ad-huser:hover {
  background: rgba(9,17,34,0.98);
  border-color: var(--b2);
  box-shadow: 0 4px 22px rgba(34,211,238,0.12);
  transform: translateY(-2px);
}
.ad-hav { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg, rgba(34,211,238,0.2), rgba(59,130,246,0.2)); border: 1px solid rgba(34,211,238,0.3); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 2px rgba(34,211,238,0.18), 0 3px 14px rgba(34,211,238,0.18); transition: box-shadow 0.25s; }
.ad-huser:hover .ad-hav { box-shadow: 0 0 0 2.5px rgba(34,211,238,0.55), 0 4px 20px rgba(34,211,238,0.35); }
.ad-hav svg { width: 15px; height: 15px; color: var(--cyan); }
.ad-hname { font-family: var(--fs); font-size: 0.88rem; font-weight: 700; color: var(--t1); letter-spacing: -0.01em; }
.ad-hrole { font-family: var(--fm); font-size: 0.62rem; color: var(--t3); }
.ad-hbtn {
  width: 40px; height: 40px; border-radius: 11px;
  background: var(--n800);
  border: 1px solid rgba(34,211,238,0.09);
  display: flex; align-items: center; justify-content: center;
  color: var(--t3); cursor: pointer; position: relative; transition: all 0.22s;
}
.ad-hbtn:hover {
  background: var(--n850);
  border-color: var(--b2);
  color: var(--cyan4);
  box-shadow: 0 4px 18px rgba(34,211,238,0.12);
  transform: translateY(-2px) scale(1.07);
}

/* Dropdown */
.ad-dd { background: var(--n850)!important; border: 1px solid var(--b2)!important; border-radius: 18px!important; box-shadow: 0 20px 60px rgba(0,0,0,0.82)!important; overflow: hidden; min-width: 220px; font-family: var(--fd)!important; }
.ad-ddhd { padding: 16px 20px 13px; border-bottom: 1px solid rgba(34,211,238,0.07); background: var(--n800); }
.ad-ddi { padding: 11px 20px!important; font-size: 0.875rem!important; font-family: var(--fd)!important; color: var(--t2)!important; cursor: pointer!important; border-radius: 0!important; transition: background 0.15s,color 0.15s!important; display: flex!important; align-items: center!important; background: var(--n850)!important; }
.ad-ddi:hover { background: var(--n800)!important; color: var(--t1)!important; }
.ad-ddi.danger { color: var(--rose)!important; }
.ad-ddi.danger:hover { background: rgba(251,113,133,0.07)!important; }

/* ══ MOBILE BAR ══ */
.ad-mobbar {
  position: fixed; top: 0; left: 0; right: 0; height: 62px;
  background: var(--n900);
  border-bottom: 1px solid rgba(34,211,238,0.08);
  display: flex; align-items: center; justify-content: space-between; z-index: 55;
}
.ad-mobbtn {
  width: 40px; height: 40px; border-radius: 11px;
  background: var(--n800);
  border: 1px solid rgba(34,211,238,0.1);
  display: flex; align-items: center; justify-content: center;
  color: var(--t3); cursor: pointer; transition: all 0.2s;
}
.ad-mobbtn:hover { background: rgba(9,17,34,0.98); border-color: var(--b2); color: var(--cyan); }
.ad-mobov { position: fixed; inset: 0; background: rgba(4,8,18,0.88); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); z-index: 48; }

/* ══ MAIN BG ══ */
.ad-main-bg { background: var(--n950); }

/* ══ RESPONSIVE ══ */
@media (min-width: 1024px) {
  .ad-mobbar { display: none !important; }
  .ad-mob-sb { display: none !important; }
  .ad-mobile-main { display: none !important; }
  .ad-desktop-main { display: flex !important; }
}
@media (max-width: 1023px) {
  .ad-sb:not(.ad-mob-sb) { display: none !important; }
  .ad-desktop-main { display: none !important; }
  .ad-mobbar { display: flex !important; }
  .ad-mob-sb { display: flex !important; }
  .ad-mobile-main { display: block !important; }
}

/* ══ PAGE TRANSITION ══ */
.ad-page { animation: pgIn 0.42s cubic-bezier(0.22,1,0.36,1) both; }
@keyframes pgIn { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
.ad-main-wrap { overflow-x: hidden; min-width: 0; }
.ad-page-inner { overflow-x: hidden; min-width: 0; }
.ad-page-inner > * { box-sizing: border-box; max-width: 100%; }
`;

/* ── Inject CSS once ── */
let cssInjected = false;
function useGlobalCSS() {
  useEffect(() => {
    if (cssInjected) return;
    cssInjected = true;
    const el = document.createElement("style");
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
  }, []);
}

/* ── Nav config ── */
const getAdminNavItems = (t) => [
  { name: t("dashboard"),       icon: LayoutDashboard, page: "AdminDashboard",          permission: null },
  { name: t("allTransactions"), icon: Globe,           page: "AdminGlobalTransactions", permission: "transactions.view" },
  { name: t("transactions"),    icon: Activity,        page: "Transactions",            permission: "transactions.view" },
  { name: t("deposits"),        icon: ArrowDownToLine, page: "Deposits",                permission: "transactions.view" },
  { name: t("withdrawals"),     icon: ArrowUpFromLine, page: "Withdrawals",             permission: "transactions.view" },
  { name: t("merchants"),       icon: Building2,       page: "Merchants",               permission: "merchants.view" },
  { name: t("engines"),         icon: Zap,             page: "AdminEngines",            permission: "engines.view" },
  { name: t("resellers"),       icon: Users,           page: "AdminResellers",          permission: "resellers.view" },
  { name: t("staff"),           icon: Shield,          page: "AdminStaff",              permission: "staff.view" },
  { name: t("roles"),           icon: Key,             page: "AdminRoles",              permission: "roles.view" },
  { name: t("providers"),       icon: Server,          page: "Providers",               permission: null, staffPermission: "providers.view" },
  { name: t("routing"),         icon: Route,           page: "Routing",                 permission: null, staffPermission: "routing.view" },
  { name: t("webhooks"),        icon: Webhook,         page: "WebhookLogs",             permission: null, staffPermission: "webhooks.view" },
  { name: t("settlements"),     icon: FileText,        page: "Settlements",             permission: null, staffPermission: "settlements.view" },
  { name: t("servers"),         icon: Cpu,             page: "AdminServers",            permission: null, staffPermission: "servers.view" },
  { name: t("notifications"),   icon: Bell,            page: "Notifications",           permission: null },
  { name: t("settings"),        icon: Settings,        page: "Settings",                permission: null, staffPermission: "settings.view" },
];

const customLayoutPages = [
  "AdminLogin","MerchantDashboard","MerchantTransactions","MerchantDeposit",
  "MerchantWithdrawals","MerchantProviders","MerchantRouting","MerchantWebhooks",
  "MerchantSettlements","MerchantSettings","MerchantLogin","ResellerDashboard",
  "ResellerTransactions","ResellerDeposits","ResellerWithdrawals","ResellerMerchants",
  "ResellerProviders","ResellerRouting","ResellerWebhooks","ResellerSettlements",
  "ResellerSettings","ResellerLogin","EngineDashboard","EngineTransactions",
  "EngineDeposits","EngineWithdrawals","EngineMerchants","EngineResellers",
  "EngineProviders","EngineRouting","EngineWebhooks","EngineSettlements",
  "EngineSettings","EngineLogin",
];

const statusColor = (s) => s === "UP" ? "#34d399" : s === "DOWN" ? "#fb7185" : "#2e4a66";
const formatMs = (ms) => (ms === null || ms === undefined) ? "--" : `${ms}ms`;

/* ══════════════════════════════════════════
   SIDEBAR INNER
══════════════════════════════════════════ */
const SidebarInner = memo(function SidebarInner({
  col, isMobile, nav, currentPage, userName, userEmail,
  t, onLogoClick, onNavClick, onToggle,
  infraStatus, apiStatus, dbStatus, loading, overallLabel,
}) {
  return (
    <>
      <Link to={createPageUrl("AdminDashboard")} onClick={onLogoClick} className="ad-logo">
        <div className="ad-orb">
          <img src="/Media.png" alt="1401 Logo" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} />
        </div>
        <AnimatePresence initial={false}>
          {(!col || isMobile) && (
            <motion.div key="lt"
              initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-10 }}
              transition={{ duration:0.2 }} style={{ overflow:"hidden" }}>
              <div className="ad-logo-name">1401</div>
              <div className="ad-logo-tag">{t("adminPanel") || "Admin Panel"}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </Link>

      <nav className="ad-nav">
        <AnimatePresence initial={false}>
          {(!col || isMobile) && (
            <motion.div key="lbl-main" className="ad-sec-lbl"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.15 }}>
              Navigation
            </motion.div>
          )}
        </AnimatePresence>

        {nav.map((item) => {
          const active = currentPage === item.page;
          const isConfig = item.page === "Providers";
          return (
            <React.Fragment key={item.page}>
              {isConfig && (
                <>
                  <div className="ad-sep" />
                  <AnimatePresence initial={false}>
                    {(!col || isMobile) && (
                      <motion.div key="lbl-cfg" className="ad-sec-lbl"
                        initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.15 }}>
                        Config
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
              <div className={(col && !isMobile) ? "ad-tipwrap" : undefined}>
                <Link
                  to={createPageUrl(item.page)}
                  onClick={(e) => onNavClick(item.page, e)}
                  className={`ad-item${active ? " on" : ""}`}
                  style={(col && !isMobile) ? { justifyContent:"center", padding:"0", height:46, borderRadius:12 } : {}}
                >
                  <div className="ad-ico"><item.icon /></div>
                  <AnimatePresence initial={false}>
                    {(!col || isMobile) && (
                      <motion.span key={`l-${item.page}`}
                        initial={{ opacity:0, width:0 }} animate={{ opacity:1, width:"auto" }} exit={{ opacity:0, width:0 }}
                        transition={{ duration:0.18 }}
                        style={{ overflow:"hidden", flex:1 }}>
                        <span className="ad-item-txt">{item.name}</span>
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Link>
                {(col && !isMobile) && <div className="ad-tip">{item.name}</div>}
              </div>
            </React.Fragment>
          );
        })}
        <div style={{ height: 8 }} />
      </nav>

      <AnimatePresence initial={false}>
        {(!col || isMobile) && (
          <motion.div key="status-card"
            initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }} exit={{ opacity:0, height:0 }}
            transition={{ duration:0.26 }} style={{ overflow:"hidden", padding: "0 10px", marginBottom: 8 }}>
            <div className="ad-status">
              <div className="ad-status-in">
                <div className="ad-status-row">
                  <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                    <div className="ad-dot-live" style={{ background: statusColor(infraStatus?.status), boxShadow:`0 0 8px ${statusColor(infraStatus?.status)}` }} />
                    <span className="ad-status-lbl">Infrastructure</span>
                  </div>
                  <span className="ad-status-badge" style={{ color: statusColor(infraStatus?.status), background:`${statusColor(infraStatus?.status)}18`, border:`1px solid ${statusColor(infraStatus?.status)}30` }}>{overallLabel}</span>
                </div>
                {[
                  { label:"API Gateway", status: apiStatus?.status, latency: apiStatus?.latencyMs },
                  { label:"Database",    status: dbStatus?.status,  latency: dbStatus?.latencyMs },
                ].map(({ label, status, latency }) => (
                  <div key={label} className="ad-status-item">
                    <span className="ad-status-name">{label}</span>
                    <div className="ad-status-val">
                      <span style={{ color: statusColor(status) }}>{loading ? "--" : formatMs(latency)}</span>
                      <div style={{ width:6, height:6, borderRadius:"50%", background: statusColor(status), boxShadow:`0 0 6px ${statusColor(status)}` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isMobile && (
        <div className="ad-togwrap">
          <motion.button
            className={`ad-tog${col ? " pill" : ""}`}
            onClick={onToggle}
            whileTap={{ scale:0.92 }}
            title={col ? "Expand sidebar" : "Collapse sidebar"}
          >
            <motion.div animate={{ rotate: col ? 180 : 0 }} transition={{ duration:0.38, ease:[0.34,1.56,0.64,1] }}>
              <ChevronLeft size={14} />
            </motion.div>
            {!col && <span className="ad-tog-txt">Collapse</span>}
          </motion.button>
        </div>
      )}
    </>
  );
});

/* ══════════════════════════════════════════
   LAYOUT CONTENT
══════════════════════════════════════════ */
function LayoutContent({ children, currentPageName, skipLayoutPages = [] }) {
  useGlobalCSS();

  const [col, setCol] = useState(false);
  const [mob, setMob] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { hasPermission } = usePermissions();
  const { data, loading } = useInfrastructureStatus();

  useEffect(() => {
    auth.me().then(setUser).catch(() => {});
  }, []);

  const allNav = getAdminNavItems(t);
  const isStaff = isStaffUser();
  const skipPages = [...customLayoutPages, ...skipLayoutPages];
  const nav = allNav.filter((item) => {
    if (isStaff) {
      const req = item.staffPermission || item.permission;
      if (!req) return false;
      return hasPermission(req);
    }
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  const handleLogout = useCallback(() => {
    const role = auth.getUserRole();
    auth.logout();
    const map = { ADMIN:"/AdminLogin", ENGINE:"/EngineLogin", RESELLER:"/ResellerLogin", MERCHANT:"/MerchantLogin" };
    window.location.href = map[role?.toUpperCase()] || "/AdminLogin";
  }, []);

  const handleNavClick = useCallback((targetPage, e) => {
    if (!mob) return;
    e.preventDefault();
    setMob(false);
    setTimeout(() => navigate(createPageUrl(targetPage)), 320);
  }, [mob, navigate]);

  const handleLogoClick = useCallback((e) => {
    if (!mob) return;
    e.preventDefault();
    setMob(false);
    setTimeout(() => navigate(createPageUrl("AdminDashboard")), 320);
  }, [mob, navigate]);

  const infraStatus  = data?.infrastructure;
  const apiStatus    = data?.apiGateway;
  const dbStatus     = data?.database;
  const overallLabel = loading ? "Checking..." : `${infraStatus?.uptimePercentage ?? "--"}%`;

  if (skipPages.includes(currentPageName)) return <>{children}</>;

  const sidebarW = col ? 68 : 258;

  const sidebarProps = { col, nav, currentPage: currentPageName, userName: user?.full_name || "Admin", userEmail: user?.email || "", t, onLogoClick: handleLogoClick, onNavClick: handleNavClick, onToggle: () => setCol(v=>!v), infraStatus, apiStatus, dbStatus, loading, overallLabel };

  return (
    <div style={{ minHeight:"100vh", background:"var(--n950)", overflowX:"hidden" }}>

      {/* MOBILE TOP BAR */}
      <div className="ad-mobbar px-4 sm:px-12 lg:px-8">
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button className="ad-mobbtn" onClick={() => setMob(v=>!v)} aria-label="Toggle menu">
            <AnimatePresence mode="wait" initial={false}>
              {mob
                ? <motion.span key="x" initial={{ rotate:-90,opacity:0 }} animate={{ rotate:0,opacity:1 }} exit={{ rotate:90,opacity:0 }} transition={{ duration:0.14 }}><X size={17}/></motion.span>
                : <motion.span key="m" initial={{ rotate:90,opacity:0 }} animate={{ rotate:0,opacity:1 }} exit={{ rotate:-90,opacity:0 }} transition={{ duration:0.14 }}><Menu size={17}/></motion.span>}
            </AnimatePresence>
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div className="ad-orb" style={{ width:34, height:34, borderRadius:10 }}><Shield /></div>
            <span style={{ fontFamily:"var(--fs)", fontWeight:800, fontSize:"1.05rem", color:"var(--t1)" }}>1401</span>
          </div>
        </div>
        <div className="ad-hbtn" style={{ position:"relative" }}>
          <Bell size={17}/>
        </div>
      </div>

      {/* MOBILE OVERLAY */}
      <AnimatePresence>
        {mob && (
          <motion.div className="ad-mobov"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            transition={{ duration:0.2 }} onClick={() => setMob(false)} />
        )}
      </AnimatePresence>

      {/* DESKTOP SIDEBAR */}
      <aside className="ad-sb" style={{ width: sidebarW, transition:"width 0.38s cubic-bezier(0.22,1,0.36,1)" }}>
        <SidebarInner {...sidebarProps} isMobile={false} />
      </aside>

      {/* DESKTOP MAIN */}
      <div
        className="ad-main-wrap ad-desktop-main ad-main-bg"
        style={{ marginLeft: sidebarW, transition:"margin-left 0.38s cubic-bezier(0.22,1,0.36,1)", minHeight:"100vh", display:"flex", flexDirection:"column", overflowX:"hidden" }}
      >
        <header className="ad-header pr-12">
          <div style={{ flex:1 }} />
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <LanguageSwitcher variant="ghost" />
            <NotificationDropdown accentColor="cyan" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ad-huser">
                  <div className="ad-hav"><User /></div>
                  <div style={{ textAlign:"left" }}>
                    <div className="ad-hname">{user?.full_name || "Admin"}</div>
                    <div className="ad-hrole">{t("administrator") || "Administrator"}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="ad-dd">
                <div className="ad-ddhd">
                  <div style={{ fontFamily:"var(--fs)", fontWeight:700, color:"var(--t1)", fontSize:"0.92rem" }}>{user?.full_name || "Admin"}</div>
                  <div style={{ fontFamily:"var(--fm)", fontSize:"0.65rem", color:"var(--t4)", marginTop:4 }}>{user?.email || "Administrator"}</div>
                </div>
                <DropdownMenuItem className="ad-ddi" onClick={() => navigate(createPageUrl("Settings"))}>
                  <Settings style={{ width:14, height:14, marginRight:10, opacity:0.5 }} />{t("settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator style={{ background:"rgba(34,211,238,0.07)" }} />
                <DropdownMenuItem className="ad-ddi danger" onClick={handleLogout}>
                  <LogOut style={{ width:14, height:14, marginRight:10 }} />{t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main key={currentPageName} className="ad-page ad-page-inner" style={{ flex:1, padding:"28px 32px 60px", overflowX:"hidden" }}>
          {children}
        </main>
      </div>

      {/* MOBILE SIDEBAR */}
      <aside
        className="ad-sb ad-mob-sb"
        style={{ width:270, transform: mob ? "translateX(0)" : "translateX(-100%)", transition:"transform 0.32s cubic-bezier(0.22,1,0.36,1)", zIndex:52 }}
      >
        <SidebarInner {...sidebarProps} col={false} isMobile={true} onToggle={() => setMob(false)} />
      </aside>

      {/* MOBILE MAIN */}
      <div className="ad-main-wrap ad-mobile-main ad-main-bg" key={currentPageName} style={{ paddingTop:62, overflowX:"hidden" }}>
        <div className="ad-page ad-page-inner" style={{ padding:"16px 14px 60px", overflowX:"hidden" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   ROOT EXPORT
══════════════════════════════════════════ */
export default function Layout({ children, currentPageName, skipLayoutPages }) {
  return (
    <LanguageProvider>
      <LayoutContent currentPageName={currentPageName} skipLayoutPages={skipLayoutPages}>{children}</LayoutContent>
    </LanguageProvider>
  );
}